import $ from "jquery";
import popoverSvgString from "./popoverSVGString";

function addPopover(
	id: string,
	selectorToAppend: string,
	title: string,
	content: string,
	placement = "right",
	customClass = "ml-4",
) {
	const $target = $(selectorToAppend);
	const isCheckboxLabel = $target.find("label").length > 0;

	// Check if browser supports native Popover API and CSS anchor positioning
	const supportsPopover = HTMLElement.prototype.hasOwnProperty("popover");
	const supportsAnchorPositioning = CSS.supports("anchor-name", "test");

	// Detect if mobile
	const isMobile = window.matchMedia("(max-width: 768px)").matches;

	if (isCheckboxLabel) {
		// For checkbox labels, insert icon inline with the text
		const $label = $target.find("label").first();

		if (supportsPopover) {
			// Use native Popover API with CSS anchor positioning when available
			const popoverId = `${id}-popover-content`;
			const anchorName = `--${id}-anchor`;

			const buttonHtml = `
        <button
          type="button"
          class="${customClass} popover-trigger"
          data-popover-id="${popoverId}"
          style="background: none; border: none; cursor: help; margin-left: 8px; padding: 0; display: inline-flex; align-items: center; vertical-align: middle;${supportsAnchorPositioning ? ` anchor-name: ${anchorName};` : ""}"
          aria-label="${title}"
        >
          ${popoverSvgString}
        </button>
      `;

			// Enhanced popover with CSS anchor positioning
			const getAnchorPositionStyles = () => {
				if (!supportsAnchorPositioning) return "";

				// Map placement to position-area values
				const anchorPositionMap: Record<string, string> = {
					right: `position-anchor: ${anchorName}; position-area: right;`,
					bottom: `position-anchor: ${anchorName}; position-area: bottom;`,
					left: `position-anchor: ${anchorName}; position-area: left;`,
					top: `position-anchor: ${anchorName}; position-area: top;`,
				};

				return anchorPositionMap[placement] || anchorPositionMap["right"];
			};

			const popoverHtml = `
        <div id="${popoverId}" popover="manual" style="
          background: white;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          min-width: 200px;
          max-width: 300px;
          margin: 0;
          ${getAnchorPositionStyles()}
        ">
          <div style="font-weight: bold; margin-bottom: 5px;">${title}</div>
          <div>${content}</div>
        </div>
      `;

			// Insert button after label text
			const labelText = $label
				.contents()
				.filter(function () {
					return this.nodeType === 3; // Text nodes
				})
				.last();

			let $button;
			if (labelText.length > 0) {
				$button = $(buttonHtml).insertAfter(labelText[0]);
			} else {
				$button = $label.append(buttonHtml).find("button");
			}

			// Append popover to body
			$("body").append(popoverHtml);

			// Position and show/hide popover
			const $popover = document.getElementById(popoverId) as any;
			let hideTimeout: any;

			const positionPopover = () => {
				// Only use manual positioning if CSS anchor positioning is not supported
				if (!supportsAnchorPositioning) {
					const rect = $button[0].getBoundingClientRect();
					const popoverRect = $popover.getBoundingClientRect();

					let left = rect.left;
					let top = rect.top;

					// Calculate position based on placement
					if (placement === "right") {
						left = rect.right + 10;
						top = rect.top + rect.height / 2 - popoverRect.height / 2;
					} else if (placement === "bottom") {
						left = rect.left + rect.width / 2 - popoverRect.width / 2;
						top = rect.bottom + 10;
					} else if (placement === "left") {
						left = rect.left - popoverRect.width - 10;
						top = rect.top + rect.height / 2 - popoverRect.height / 2;
					} else if (placement === "top") {
						left = rect.left + rect.width / 2 - popoverRect.width / 2;
						top = rect.top - popoverRect.height - 10;
					}

					// Keep within viewport
					const viewportWidth = window.innerWidth;
					const viewportHeight = window.innerHeight;

					if (left + popoverRect.width > viewportWidth - 10) {
						left = viewportWidth - popoverRect.width - 10;
					}
					if (left < 10) left = 10;
					if (top + popoverRect.height > viewportHeight - 10) {
						top = viewportHeight - popoverRect.height - 10;
					}
					if (top < 10) top = 10;

					$popover.style.left = `${left}px`;
					$popover.style.top = `${top}px`;
				}
				// If CSS anchor positioning is supported, positioning is handled by CSS
			};

			const showPopover = () => {
				clearTimeout(hideTimeout);
				$popover.showPopover();
				positionPopover();
			};

			const hidePopover = () => {
				hideTimeout = setTimeout(() => {
					if ($popover.matches(":popover-open")) {
						$popover.hidePopover();
					}
				}, 100);
			};

			if (isMobile) {
				// Click behavior for mobile
				$button.on("click", (e) => {
					e.preventDefault();
					e.stopPropagation();
					if ($popover.matches(":popover-open")) {
						$popover.hidePopover();
					} else {
						showPopover();
					}
				});

				// Hide when clicking outside
				$(document).on("click", (e) => {
					if (!$(e.target).closest($button).length && !$(e.target).closest($popover).length) {
						if ($popover.matches(":popover-open")) {
							$popover.hidePopover();
						}
					}
				});
			} else {
				// Hover behavior for desktop
				$button.on("mouseenter", showPopover);
				$button.on("mouseleave", hidePopover);
				$($popover).on("mouseenter", () => clearTimeout(hideTimeout));
				$($popover).on("mouseleave", hidePopover);
			}
		} else {
			// Fallback: Use CSS-only solution with :hover and :focus-within
			const tooltipHtml = `
        <span
          class="${customClass} popover-trigger"
          style="margin-left: 8px; position: relative; display: inline-flex; cursor: help;"
          tabindex="0"
          aria-label="${title}"
        >
          ${popoverSvgString}
          <span class="popover-content" style="
            visibility: hidden;
            opacity: 0;
            position: absolute;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
            min-width: 200px;
            max-width: 300px;
            z-index: 10000;
            ${placement === "right" ? "left: 100%; top: 50%; transform: translateY(-50%); margin-left: 10px;" : ""}
            ${placement === "bottom" ? "top: 100%; left: 50%; transform: translateX(-50%); margin-top: 10px;" : ""}
            ${placement === "left" ? "right: 100%; top: 50%; transform: translateY(-50%); margin-right: 10px;" : ""}
            ${placement === "top" ? "bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 10px;" : ""}
            transition: opacity 0.2s, visibility 0.2s;
          ">
            <div style="font-weight: bold; margin-bottom: 5px;">${title}</div>
            <div>${content}</div>
          </span>
        </span>
      `;

			// Insert after label text
			const labelText = $label
				.contents()
				.filter(function () {
					return this.nodeType === 3;
				})
				.last();

			if (labelText.length > 0) {
				$(tooltipHtml).insertAfter(labelText[0]);
			} else {
				$label.append(tooltipHtml);
			}

			// Add CSS for hover/focus interaction
			const styleId = "popover-hover-styles";
			if (!document.getElementById(styleId)) {
				const style = document.createElement("style");
				style.id = styleId;
				style.textContent = `
          .popover-trigger:hover .popover-content,
          .popover-trigger:focus .popover-content,
          .popover-trigger:focus-within .popover-content {
            visibility: visible !important;
            opacity: 1 !important;
          }
        `;
				document.head.appendChild(style);
			}
		}
	} else {
		// For non-checkbox elements (like buttons)
		$target.css({
			position: "relative",
			overflow: "visible",
		});

		if (supportsPopover) {
			// Use native Popover API with CSS anchor positioning when available
			const popoverId = `${id}-popover-content`;
			const anchorName = `--${id}-anchor`;

			const buttonHtml = `
        <button
          type="button"
          class="${customClass} popover-trigger"
          data-popover-id="${popoverId}"
          style="position: absolute; right: -25px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: help; padding: 0; display: inline-flex;${supportsAnchorPositioning ? ` anchor-name: ${anchorName};` : ""}"
          aria-label="${title}"
        >
          ${popoverSvgString}
        </button>
      `;

			// Enhanced popover with CSS anchor positioning for non-checkbox elements
			const getAnchorPositionStyles = () => {
				if (!supportsAnchorPositioning) return "";

				const anchorPositionMap: Record<string, string> = {
					right: `position-anchor: ${anchorName}; position-area: right;`,
					bottom: `position-anchor: ${anchorName}; position-area: bottom;`,
					left: `position-anchor: ${anchorName}; position-area: left;`,
					top: `position-anchor: ${anchorName}; position-area: top;`,
				};

				return anchorPositionMap[placement] || anchorPositionMap["right"];
			};

			const popoverHtml = `
        <div id="${popoverId}" popover="manual" style="
          background: white;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          min-width: 200px;
          max-width: 300px;
          margin: 0;
          ${getAnchorPositionStyles()}
        ">
          <div style="font-weight: bold; margin-bottom: 5px;">${title}</div>
          <div>${content}</div>
        </div>
      `;

			const $button = $(buttonHtml).appendTo($target);
			$("body").append(popoverHtml);

			// Position and show/hide popover
			const $popover = document.getElementById(popoverId) as any;
			let hideTimeout: any;

			const positionPopover = () => {
				// Only use manual positioning if CSS anchor positioning is not supported
				if (!supportsAnchorPositioning) {
					const rect = $button[0].getBoundingClientRect();
					const popoverRect = $popover.getBoundingClientRect();

					let left = rect.left;
					let top = rect.top;

					// Calculate position based on placement
					if (placement === "right") {
						left = rect.right + 10;
						top = rect.top + rect.height / 2 - popoverRect.height / 2;
					} else if (placement === "bottom") {
						left = rect.left + rect.width / 2 - popoverRect.width / 2;
						top = rect.bottom + 10;
					} else if (placement === "left") {
						left = rect.left - popoverRect.width - 10;
						top = rect.top + rect.height / 2 - popoverRect.height / 2;
					} else if (placement === "top") {
						left = rect.left + rect.width / 2 - popoverRect.width / 2;
						top = rect.top - popoverRect.height - 10;
					}

					// Keep within viewport
					const viewportWidth = window.innerWidth;
					const viewportHeight = window.innerHeight;

					if (left + popoverRect.width > viewportWidth - 10) {
						left = viewportWidth - popoverRect.width - 10;
					}
					if (left < 10) left = 10;
					if (top + popoverRect.height > viewportHeight - 10) {
						top = viewportHeight - popoverRect.height - 10;
					}
					if (top < 10) top = 10;

					$popover.style.left = `${left}px`;
					$popover.style.top = `${top}px`;
				}
				// If CSS anchor positioning is supported, positioning is handled by CSS
			};

			const showPopover = () => {
				clearTimeout(hideTimeout);
				$popover.showPopover();
				positionPopover();
			};

			const hidePopover = () => {
				hideTimeout = setTimeout(() => {
					if ($popover.matches(":popover-open")) {
						$popover.hidePopover();
					}
				}, 100);
			};

			if (isMobile) {
				// Click behavior for mobile
				$button.on("click", (e) => {
					e.preventDefault();
					e.stopPropagation();
					if ($popover.matches(":popover-open")) {
						$popover.hidePopover();
					} else {
						showPopover();
					}
				});

				// Hide when clicking outside
				$(document).on("click", (e) => {
					if (!$(e.target).closest($button).length && !$(e.target).closest($popover).length) {
						if ($popover.matches(":popover-open")) {
							$popover.hidePopover();
						}
					}
				});
			} else {
				// Hover behavior for desktop
				$button.on("mouseenter", showPopover);
				$button.on("mouseleave", hidePopover);
				$($popover).on("mouseenter", () => clearTimeout(hideTimeout));
				$($popover).on("mouseleave", hidePopover);
			}
		} else {
			// Fallback: CSS-only solution
			const tooltipHtml = `
        <div
          class="${customClass} popover-trigger"
          style="position: absolute; right: -25px; top: 50%; transform: translateY(-50%); display: inline-flex; cursor: help;"
          tabindex="0"
          aria-label="${title}"
        >
          ${popoverSvgString}
          <div class="popover-content" style="
            visibility: hidden;
            opacity: 0;
            position: absolute;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
            min-width: 200px;
            z-index: 10000;
            ${placement === "right" ? "left: 100%; top: 50%; transform: translateY(-50%); margin-left: 10px;" : ""}
            ${placement === "bottom" ? "top: 100%; left: 50%; transform: translateX(-50%); margin-top: 10px;" : ""}
            ${placement === "left" ? "right: 100%; top: 50%; transform: translateY(-50%); margin-right: 10px;" : ""}
            ${placement === "top" ? "bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 10px;" : ""}
            transition: opacity 0.2s, visibility 0.2s;
          ">
            <div style="font-weight: bold; margin-bottom: 5px;">${title}</div>
            <div>${content}</div>
          </div>
        </div>
      `;

			$target.append(tooltipHtml);

			// Add CSS for hover/focus interaction
			const styleId = "popover-hover-styles";
			if (!document.getElementById(styleId)) {
				const style = document.createElement("style");
				style.id = styleId;
				style.textContent = `
          .popover-trigger:hover .popover-content,
          .popover-trigger:focus .popover-content,
          .popover-trigger:focus-within .popover-content {
            visibility: visible !important;
            opacity: 1 !important;
          }
        `;
				document.head.appendChild(style);
			}
		}
	}
}

export default addPopover;
